import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { DialogTitle, Grid, MenuItem, Modal, TextField } from "@mui/material";
import { Stack } from "@mui/system";
import AuthModal from "../../studioxr/auth/AuthModal";
import { ActionButton } from "../../buttons/ButtonStyles";
import { createProjectScheme, editProjectScheme } from "../../v2/apis/projectsSchemeApi";

const textFieldProps: any = {
  variant: "outlined",
  margin: "normal",
  size: "small",
  fullWidth: true,
  type: "text",
  sx: { paddingRight: "10px", width: "98%", marginTop: "8px" },
};

// Custom validation function to replace Yup
const validateForm = (values: any) => {
  const errors: any = {};
  
  if (!values.name) {
    errors.name = "Project name is required";
  } else if (values.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
  }
  
  return errors;
};

export const ProjectSchemeCreator = ({ project, setShowCreatorModal, updateProject }) => {
  const [saving, setSaving] = useState(false);
  const [projectExists, setProjectExists] = useState(false);

  const handleClose = () => {
    setShowCreatorModal(false);
  };

  useEffect(() => {
    if (project.id) {
      setProjectExists(true);
    } else {
      setProjectExists(false);
    }
  }, [project]);

  const handleSubmit = async (values: any) => {
    setSaving(true);
    if (project?.id) {
      const updatedProject = await editProjectScheme({ ...project, ...values });
      updateProject(updatedProject);
    } else {
      const newProject = await createProjectScheme({ ...values, id: project.id });
      updateProject(newProject,true);
    }
    setSaving(false);
    setShowCreatorModal(false);
  };

  return (
    <Modal
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={true}
      onClose={handleClose}
    >
      <Grid
        sx={{
          // Size to content with constraints
          width: 'auto',
          maxWidth: '90vw',
          maxHeight: '90vh',
          minWidth: '400px',
          margin: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'auto',
          minHeight: 'fit-content',
          padding: '10px'
        }}
      >
        
      <Formik
        initialValues={{
          name: project?.name || "New Project",
          type: project?.type || "",
          phase: project?.phase || "",
          startDate: project?.startDate || "",
          location: project?.location || "",
          description: project?.description || "",
        }}
        validate={validateForm}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <Stack sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <DialogTitle sx={{ paddingLeft: "0px", fontWeight: "bold" }}>
                  {!projectExists ? "Create New Project" : "Edit Project"}
                </DialogTitle>
              </Grid>

              {/* Project Name */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="name"
                label="Project Name"
                name="name"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
              />

              {/* Project Type */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="type"
                label="Project Type"
                name="type"
                select
                error={touched.type && !!errors.type}
                helperText={touched.type && errors.type}
              >
                <MenuItem value="Commercial">Commercial</MenuItem>
                <MenuItem value="Residential">Residential</MenuItem>
                <MenuItem value="Hospitality">Hospitality</MenuItem>
              </Field>

              {/* Project Phase */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="phase"
                label="Project Phase"
                name="phase"
                select
                error={touched.phase && !!errors.phase}
                helperText={touched.phase && errors.phase}
              >
                <MenuItem value="Concept Design">Concept Design</MenuItem>
                <MenuItem value="Schematic Design">Schematic Design</MenuItem>
                <MenuItem value="Design Development">Design Development</MenuItem>
                <MenuItem value="Construction Documents">Construction Documents</MenuItem>
                <MenuItem value="Construction Administration">Construction Administration</MenuItem>
              </Field>

              {/* Target Date */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="startDate"
                label="Target Date"
                name="startDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={touched.startDate && !!errors.startDate}
                helperText={touched.startDate && errors.startDate}
              />

              {/* Project Location */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="location"
                label="Project Location"
                name="location"
                error={touched.location && !!errors.location}
                helperText={touched.location && errors.location}
              />

              {/* Project Description */}
              <Field
                as={TextField}
                {...textFieldProps}
                id="description"
                label="Project Description"
                name="description"
                multiline
                rows={6}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
              />

              {/* Action Buttons */}
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "20px",
                  marginBottom: "20px",
                  gap: "16px",
                }}
              >
                <ActionButton variant="contained" onClick={handleClose}>
                  Cancel
                </ActionButton>
                <ActionButton disabled={isSubmitting || saving} variant="contained" type="submit">
                  {saving ? "Saving..." : "Save"}
                </ActionButton>
              </Grid>
            </Stack>
          </Form>
        )}
      </Formik>
      </Grid>
    </Modal>
  );
};